// ===================================
// JOB DESCRIPTION INPUT COMPONENT - STAGE B FIX
// src/components/JobDescriptionInput.js
// Input job description text or upload file
// Stage B: Added documentId prop, improved onChange callback
// ===================================

import React, { useState, useEffect } from 'react';
import FileUpload from './FileUpload';
import documentApi from '../services/documentApi';
import './JobDescriptionInput.css';

const JobDescriptionInput = ({ 
  value = '', 
  documentId = null,  // STAGE B: Accept existing document ID
  companyName = '',
  requireCompanyName = false,
  onChange 
}) => {
  const [inputMode, setInputMode] = useState('paste'); // 'paste' or 'upload'
  const [text, setText] = useState(value);
  const [company, setCompany] = useState(companyName);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);
  const [extracted, setExtracted] = useState(null);

  // Sync initial values
  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    setCompany(companyName);
  }, [companyName]);

  // STAGE B: Handle existing documentId prop
  useEffect(() => {
    if (documentId && !uploadedFile) {
      setUploadedFile({ id: documentId, name: 'Previously uploaded' });
      setInputMode('upload');
    }
  }, [documentId, uploadedFile]);

  // STAGE B: Notify parent of changes - always include all fields
  const notifyChange = (newText, newCompany, docId = null) => {
    if (onChange) {
      onChange({
        text: newText,
        companyName: newCompany,
        documentId: docId,
        // Legacy field names for compatibility
        id: docId
      });
    }
  };

  // Handle text paste/type
  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    notifyChange(newText, company, uploadedFile?.id || null);
  };

  // Handle company name change
  const handleCompanyChange = (e) => {
    const newCompany = e.target.value;
    setCompany(newCompany);
    notifyChange(text, newCompany, uploadedFile?.id || null);
  };

  // Handle file upload
  const handleFileSelect = async (file) => {
    try {
      setUploading(true);
      setError(null);
      
      const response = await documentApi.uploadJobDescription(file);
      
      setUploadedFile({
        id: response.document.id,
        name: response.document.original_name
      });
      
      // If AI extracted data, show it
      if (response.extracted) {
        setExtracted(response.extracted);
        
        // Auto-fill company name if extracted and not already set
        if (response.extracted.company && !company) {
          setCompany(response.extracted.company);
          notifyChange(text, response.extracted.company, response.document.id);
        } else {
          notifyChange(text, company, response.document.id);
        }
        
        // Auto-fill text if extracted
        if (response.extracted.text) {
          setText(response.extracted.text);
          notifyChange(
            response.extracted.text, 
            company || response.extracted.company || '', 
            response.document.id
          );
        }
      } else {
        notifyChange(text, company, response.document.id);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Clear uploaded file
  const handleClearFile = () => {
    setUploadedFile(null);
    setExtracted(null);
    notifyChange(text, company, null);
  };

  return (
    <div className="jd-input">
      {/* Company Name Input */}
      <div className="form-group">
        <label>
          Company Name {requireCompanyName && <span className="required">*</span>}
        </label>
        <input
          type="text"
          value={company}
          onChange={handleCompanyChange}
          placeholder="e.g., Google, Microsoft, Startup Inc."
          className={`text-input ${requireCompanyName && !company.trim() ? 'input-warning' : ''}`}
        />
        <small>Interview questions will reference this company</small>
      </div>

      {/* Input Mode Toggle */}
      <div className="input-mode-toggle">
        <button
          type="button"
          className={`mode-btn ${inputMode === 'paste' ? 'active' : ''}`}
          onClick={() => setInputMode('paste')}
        >
          📝 Paste Text
        </button>
        <button
          type="button"
          className={`mode-btn ${inputMode === 'upload' ? 'active' : ''}`}
          onClick={() => setInputMode('upload')}
        >
          📤 Upload File
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️</span> {error}
        </div>
      )}

      {/* Paste Mode */}
      {inputMode === 'paste' && (
        <div className="form-group">
          <label>Job Description</label>
          <textarea
            value={text}
            onChange={handleTextChange}
            placeholder="Paste the job description here...

Example:
We are looking for a Senior Software Engineer to join our team...

Requirements:
- 5+ years of experience
- Proficiency in React, Node.js
- Strong communication skills"
            rows={10}
            className="text-area"
          />
          <div className="char-count">
            {text.length} characters
            {text.length > 0 && text.length < 100 && (
              <span className="warning"> (minimum 100 recommended)</span>
            )}
            {text.length >= 100 && (
              <span className="success"> ✓</span>
            )}
          </div>
        </div>
      )}

      {/* Upload Mode */}
      {inputMode === 'upload' && (
        <div className="upload-mode">
          {uploadedFile ? (
            <div className="uploaded-file">
              <div className="file-info">
                <span className="file-icon">📄</span>
                <span className="file-name">{uploadedFile.name}</span>
                <button 
                  type="button"
                  className="clear-btn"
                  onClick={handleClearFile}
                >
                  ✕
                </button>
              </div>
              
              {extracted && (
                <div className="extracted-info">
                  <h4>Extracted Information</h4>
                  {extracted.title && (
                    <div className="extracted-item">
                      <span className="label">Job Title:</span>
                      <span className="value">{extracted.title}</span>
                    </div>
                  )}
                  {extracted.company && (
                    <div className="extracted-item">
                      <span className="label">Company:</span>
                      <span className="value">{extracted.company}</span>
                    </div>
                  )}
                  {extracted.requirements?.length > 0 && (
                    <div className="extracted-item">
                      <span className="label">Key Requirements:</span>
                      <ul className="requirements-list">
                        {extracted.requirements.slice(0, 5).map((req, i) => (
                          <li key={i}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <FileUpload
              onFileSelect={handleFileSelect}
              loading={uploading}
              label="Upload Job Description"
              description="PDF, DOC, DOCX, or TXT"
              allowedTypes={['PDF', 'DOC', 'DOCX', 'TXT']}
            />
          )}
        </div>
      )}

      {/* Tips */}
      <div className="jd-tips">
        <h4>💡 Tips for better results</h4>
        <ul>
          <li>Include the full job description with requirements</li>
          <li>Responsibilities section helps tailor questions</li>
          <li>Include company name for industry-specific questions</li>
        </ul>
      </div>
    </div>
  );
};

export default JobDescriptionInput;